import { buildUrl } from "../lib/utils"

const BASE_URL = (import.meta.env.VITE_BASE_BACKEND_URL || '/api') as string
const AUTH_BASE = `${BASE_URL}/auth`
const AUTH_REGISTRATION_BASE = `${AUTH_BASE}/registration`
const HOMEOWNER_BASE = `${BASE_URL}/homeowner`
const HOME_BASE = `${HOMEOWNER_BASE}/home`

export const AUTH = {
    login: `${AUTH_BASE}/login`,
    checkEmail: `${AUTH_BASE}/check-email`,
    sendLink: `${AUTH_BASE}/registration/send-login-link`,
    verify_otp: `${AUTH_BASE}/verify-otp`,
    send_otp: `${AUTH_REGISTRATION_BASE}/send-otp`,
    user: `${AUTH_BASE}/google/callback`,
    registration: {
        progress: (userEmail: string) =>
            `${AUTH_REGISTRATION_BASE}/${userEmail}/progress`,
        forgotPassword: `${AUTH_REGISTRATION_BASE}/forgot-password`,
        signup: {
            step1: `${AUTH_REGISTRATION_BASE}/step1`,
        },
        resetPassword: (email: string, token: string) =>
            buildUrl(`${AUTH_REGISTRATION_BASE}/reset-password`, {
                email,
                token,
            }),
    },
    google: {
        base: `${AUTH_BASE}/google`,
        callback: `${AUTH_BASE}/google/callback`,
        complete: `${AUTH_BASE}/google/complete`,
        pendingSession: `${AUTH_BASE}/google/pending-session`,
    },
}

export const PROFILE = {
    base: (userId: number) => `${BASE_URL}/my-profile/${userId}`,
    location: (userId: number) => `${BASE_URL}/my-profile/${userId}/location`,
    stm: (userId: number) => `${BASE_URL}/my-profile/${userId}/aim-stm`,
    baseProfile: (userId: number) => `${BASE_URL}/my-profile/${userId}/base`,
}

export const WORK_ORDERS = {
    create: `${BASE_URL}/work-orders/create`,
    getAll: `${BASE_URL}/work-orders`,
    byId: (id: number) => `${BASE_URL}/work-orders/${id}`,
    update: (id: number) => `${BASE_URL}/work-orders/${id}`,
    notes: (id: number) => `${BASE_URL}/work-orders/${id}/notes`,
    teamLead: `${BASE_URL}/work-orders/team-lead`,
    workScopeOptions: `${BASE_URL}/work-orders/work-scope-options`,
    jobBoard: `${BASE_URL}/work-orders/job-board`,
    availableVolunteers: `${BASE_URL}/work-orders/available-volunteers`,
    dailyVolunteers: (id: number) => `${BASE_URL}/work-orders/${id}/daily-volunteers`,
    approvalStatus: (id: number) => `${BASE_URL}/work-orders/${id}/approval-status`,
    jobBoardTeams: `${BASE_URL}/work-orders/job-board/teams`,
    jobBoardTeam: (id: number) => `${BASE_URL}/work-orders/job-board/teams/${id}`,
}

export const HOME_OWNERS = {
    base: HOMEOWNER_BASE,
    byId: (id: number) => `${HOMEOWNER_BASE}/${id}`,
    byUserId: (userId: number) => `${HOMEOWNER_BASE}/by-user/${userId}`,
}

export const HOME = {
    base: HOME_BASE,
    details: (homeId: number) => `${HOME_BASE}/${homeId}/details`,
    update: (homeId: number) => `${HOME_BASE}/${homeId}`,
    uploadPhotos: (homeId: number) => `${HOME_BASE}/${homeId}/photos`,
    create: {
        step1: (homeOwnerId: number) => `${HOME_BASE}/${homeOwnerId}/step1`,
        step2: (homeOwnerId: number) => `${HOME_BASE}/${homeOwnerId}/step2`,
        step3: (homeOwnerId: number) => `${HOME_BASE}/${homeOwnerId}/step3`,
    },
}

export const ADMIN = {
    base: `${BASE_URL}/volunteer`,
    byId: (id: number) => `${BASE_URL}/volunteer/${id}`,
    status: (id: number) => `${BASE_URL}/volunteer/${id}/status`,
    locations: `${BASE_URL}/volunteer/locations`,
    organizations: `${BASE_URL}/volunteer/organizations`,
    /** GET single non-homeowner user by user id (for Update User dialog). */
    nonHomeownerUserById: (userId: number) => `${BASE_URL}/volunteer/user/${userId}`,
    /** Volunteer table rows (minimal fields for filter-search and group members tables) */
    tableRows: `${BASE_URL}/volunteer/table-rows`,
    /** Group volunteers: add/remove (POST/DELETE) */
    groupVolunteersBase: `${BASE_URL}/volunteer/group-volunteers`,
}

export const USER = {
    byId: (userId: number) => `${BASE_URL}/user/${userId}`,
    update: (userId: number) => `${BASE_URL}/user/${userId}`,
    delete: (userId: number) => `${BASE_URL}/user/${userId}`,
}

export const SETTINGS = {
    byId: (userId: number) => `${BASE_URL}/settings/${userId}`,
    setupTwoFactAuth: (userId: number) =>
        `${BASE_URL}/settings/${userId}/setup-2fa`,
    verifyTwoFactAuthCode: (userId: number) =>
        `${BASE_URL}/settings/${userId}/verify-2fa`,
}

export const NOTIFICATIONS = {
    byUser: (userId: number) => `${BASE_URL}/notifications/user/${userId}`,
    markAsRead: (userId: number) => `${BASE_URL}/notifications/mark-as-read/${userId}`,
    delete: (notificationId: number) => `${BASE_URL}/notifications/${notificationId}`,
}

export const PHOTOS = {
    base: `${BASE_URL}/photos`,
    byHome: (homeId: number) => `${BASE_URL}/photos/home/${homeId}`,
    byId: (photoId: number) => `${BASE_URL}/photos/${photoId}`,
    upload: `${BASE_URL}/photos/upload`,
    uploadMultiple: `${BASE_URL}/photos/upload-multiple`,
    /** Single base photo (Hub); sequential uploads recommended on weak networks */
    baseUpload: `${BASE_URL}/photos/base/upload`,
    /** Base photos (Hub) - upload directly to backend to bypass Next.js/Vercel body size limits */
    baseUploadMultiple: `${BASE_URL}/photos/base/upload-multiple`,
    /** Resumable large video: ~10 MiB S3 multipart parts (requires S3 CORS + ETag expose) */
    videoMultipartHomeInit: `${BASE_URL}/photos/video/multipart/home/init`,
    videoMultipartBaseInit: `${BASE_URL}/photos/base/video/multipart/init`,
    videoMultipartSignPart: `${BASE_URL}/photos/video/multipart/sign-part`,
    videoMultipartRegisterPart: `${BASE_URL}/photos/video/multipart/register-part`,
    videoMultipartComplete: `${BASE_URL}/photos/video/multipart/complete`,
    stats: (homeId: number) => `${BASE_URL}/photos/home/${homeId}/stats`,
    workScopeComplete: (homeId: number) => `${BASE_URL}/photos/home/${homeId}/work-scope-complete`,
    workScopeOptions: (homeId: number) => `${BASE_URL}/photos/home/${homeId}/work-scope-options`,
    workScopeCompleteTag: (photoId: number) => `${BASE_URL}/photos/${photoId}/work-scope-complete`,
    delete: (photoId: number) => `${BASE_URL}/photos/${photoId}`,
    deletePermanent: (photoId: number) => `${BASE_URL}/photos/${photoId}/permanent`,
}

/** PII reveal endpoint (Admin-only, audit-logged) */
export const PII = {
    reveal: (volunteerId: number, fieldName: string) =>
        `${BASE_URL}/pii/volunteer/${volunteerId}/reveal/${fieldName}`,
}

/** Volunteer group photos (no work order link) */
export const GROUP_PHOTOS = {
    byGroup: (groupId: string) => `${BASE_URL}/photos/group/${groupId}`,
    uploadMultiple: `${BASE_URL}/photos/group/upload-multiple`,
    byId: (id: number) => `${BASE_URL}/photos/group/photo/${id}`,
    delete: (id: number) => `${BASE_URL}/photos/group/photo/${id}`,
    deletePermanent: (id: number) => `${BASE_URL}/photos/group/photo/${id}/permanent`,
}

export const FEATURE_FLAGS = {
    evaluate: `${BASE_URL}/feature-flags/evaluate`,
    check: (key: string) => `${BASE_URL}/feature-flags/check/${key}`,
    base: `${BASE_URL}/feature-flags`,
    byId: (id: number) => `${BASE_URL}/feature-flags/${id}`,
    users: (id: number, userId: number) => `${BASE_URL}/feature-flags/${id}/users/${userId}`,
    searchUsers: `${BASE_URL}/feature-flags/users/search`,
}